import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  zIndex: 22000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "14px 12px",
};

const shellStyle = {
  width: "100%",
  maxWidth: "680px",
  borderRadius: "16px",
  padding: "28px",
  background: "var(--erp-modal-bg, #0e1730)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
  borderTop: "3px solid #3DB6E9",
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100dvh - 28px)",
  overflowY: "auto",
};

const contentWrapperStyle = {};

const sectionStyle = {
  marginTop: "18px",
  borderRadius: "12px",
  padding: "18px",
  background: "var(--erp-modal-section, #121f3d)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 800,
  color: "var(--erp-modal-title, #f2f6ff)",
};

const sectionTextStyle = {
  margin: "4px 0 0",
  fontSize: "0.78rem",
  color: "var(--erp-modal-muted, #aebbd0)",
};

const tabContainerStyle = {
  display: "flex",
  background: "var(--erp-modal-section, #121f3d)",
  borderRadius: "12px",
  padding: "4px",
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
};

const tabButtonStyle = (active) => ({
  flex: 1,
  borderRadius: "10px",
  border: "none",
  padding: "10px",
  fontWeight: 700,
  fontSize: "0.85rem",
  cursor: "pointer",
  color: active ? "#ffffff" : "var(--erp-modal-text, #dfe8f7)",
  background: active ? "var(--erp-modal-primary, #223A7A)" : "transparent",
  boxShadow: active ? "inset 3px 0 0 var(--erp-modal-primary-border, #3DB6E9)" : "none",
  transition: "all 0.2s ease",
});

const fieldGridStyle = {
  marginTop: "12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
};

const labelStyle = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--erp-modal-text, #dfe8f7)",
  marginBottom: "4px",
  display: "block",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "var(--erp-modal-input, #152344)",
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  color: "var(--erp-modal-text, #dfe8f7)",
  fontSize: "0.95rem",
};

const actionsRowStyle = {
  marginTop: "16px",
  display: "flex",
  gap: "12px",
};

const primaryButtonStyle = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "1px solid var(--erp-modal-primary-border, #3DB6E9)",
  background: "var(--erp-modal-primary, #223A7A)",
  color: "#ffffff",
};

const secondaryButtonStyle = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  background: "var(--erp-modal-bg, #0e1730)",
  color: "var(--erp-modal-text, #dfe8f7)",
};

const helperGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "12px",
  marginTop: "12px",
};

const savingOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  fontWeight: "bold",
  zIndex: 23000,
  pointerEvents: "none",
};

const WEEKDAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const modeTabs = [
  { key: "single", label: "Single Date" },
  { key: "range", label: "Range" },
  { key: "weekdays", label: "Weekdays" },
  { key: "customRange", label: "Variable Range Entry" },
];

const parseStayValue = (raw) => {
  if (raw === "" || raw === null || typeof raw === "undefined") return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
};

const parseIsoLocalDate = (raw) => {
  const str = String(raw || "").slice(0, 10);
  const [year, month, day] = str.split("-").map((n) => Number(n));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const date = new Date(year, month - 1, day);
  if (!Number.isFinite(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const toIsoLocalDate = (date) => {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const buildDayPayload = ({ date, rawValue, clearFlag, field, roomRateId }) => {
  if (clearFlag) {
    return {
      room_rate_id: roomRateId,
      start_date: date,
      end_date: date,
      [field]: null,
    };
  }
  const parsed = parseStayValue(rawValue);
  if (parsed === null) return null;
  return {
    room_rate_id: roomRateId,
    start_date: date,
    end_date: date,
    [field]: parsed,
  };
};

const daysBetween = (start, end) => {
  const result = [];
  const cursor = new Date(start);
  const until = new Date(end);
  cursor.setHours(0, 0, 0, 0);
  until.setHours(0, 0, 0, 0);
  while (cursor <= until) {
    result.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

export default function NewStayConstraintPopup({ show, onClose, roomRate, type }) {
  const [localData, setLocalData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show && roomRate) {
      setLocalData({
        mode: "single",
        start_date: "",
        end_date: "",
        value: "",
        remove: false,
        room_rate_id: roomRate.id,
        room_rate: roomRate,
      });
    }
  }, [show, roomRate]);

  if (!show || !localData) return null;

  const label = type === "min" ? "Min Stay" : "Max Stay";
  const field = type === "min" ? "min_stay" : "max_stay";

  const postConstraint = (payload) =>
    axios.patch(
      `${process.env.REACT_APP_API_URL}/api/rate-prices/${type === "min" ? "set-min-stay" : "set-max-stay"}`,
      payload
    );

  const handleMultiplePayloads = async (payloads) => {
    for (const payload of payloads) {
      await postConstraint(payload);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const validFrom = parseIsoLocalDate(localData.room_rate?.valid_from);
      const validTo = parseIsoLocalDate(localData.room_rate?.valid_to);

      if (localData.mode === "weekdays") {
        const start = parseIsoLocalDate(localData.start_date);
        const end = parseIsoLocalDate(localData.end_date);
        if (!start || !end || end < start) {
          alert("Please select valid start and end dates.");
          setIsSaving(false);
          return;
        }
        if ((validFrom && start < validFrom) || (validTo && end > validTo)) {
          alert(`Dates must be within:\n${localData.room_rate.valid_from} to ${localData.room_rate.valid_to}`);
          setIsSaving(false);
          return;
        }
        const dates = daysBetween(start, end);
        if (dates.length > 366) {
          alert("Maximum 366 days allowed.");
          setIsSaving(false);
          return;
        }
        const payloads = [];
        for (const dateObj of dates) {
          const iso = toIsoLocalDate(dateObj);
          const dow = WEEKDAYS[dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1];
          const valueKey = `wd_value_${dow.key}`;
          const clearKey = `wd_clear_${dow.key}`;
          const payload = buildDayPayload({
            date: iso,
            rawValue: localData[valueKey],
            clearFlag: !!localData[clearKey],
            field,
            roomRateId: localData.room_rate_id,
          });
          if (payload) payloads.push(payload);
        }
        if (!payloads.length) {
          alert("No weekdays selected or filled.");
          setIsSaving(false);
          return;
        }
        await handleMultiplePayloads(payloads);
        alert(`${label} saved successfully (weekdays).`);
        onClose && onClose(true);
        return;
      }

      if (localData.mode === "customRange") {
        const start = parseIsoLocalDate(localData.start_date);
        const end = parseIsoLocalDate(localData.end_date);
        if (!start || !end) {
          alert("Please select valid start and end dates.");
          setIsSaving(false);
          return;
        }
        if (end < start) {
          alert("End date must be after start date.");
          setIsSaving(false);
          return;
        }
        const dates = daysBetween(start, end);
        if (dates.length > 30) {
          alert("Maximum 30 days allowed.");
          setIsSaving(false);
          return;
        }
        const payloads = [];
        dates.forEach((dateObj, idx) => {
          const iso = toIsoLocalDate(dateObj);
          const valueKey = `multi_value_${idx}`;
          const clearKey = `multi_clear_${idx}`;
          const payload = buildDayPayload({
            date: iso,
            rawValue: localData[valueKey],
            clearFlag: !!localData[clearKey],
            field,
            roomRateId: localData.room_rate_id,
          });
          if (payload) payloads.push(payload);
        });
        if (!payloads.length) {
          alert("No valid days to save.");
          setIsSaving(false);
          return;
        }
        await handleMultiplePayloads(payloads);
        alert(`${payloads.length} ${label} updates saved.`);
        onClose && onClose(true);
        return;
      }

      const start = parseIsoLocalDate(localData.start_date);
      const end = localData.mode === "single"
        ? start
        : parseIsoLocalDate(localData.end_date);
      if (!start || !end) {
        alert("Please select valid start and end dates.");
        setIsSaving(false);
        return;
      }
      if ((validFrom && start < validFrom) || (validTo && end > validTo)) {
        alert(`Dates must be within:\n${localData.room_rate.valid_from} to ${localData.room_rate.valid_to}`);
        setIsSaving(false);
        return;
      }
      await postConstraint({
        room_rate_id: localData.room_rate_id,
        start_date: localData.start_date,
        end_date: localData.mode === "single" ? localData.start_date : localData.end_date,
        [field]: localData.remove ? null : parseStayValue(localData.value),
      });
      alert(`${label} saved successfully.`);
      onClose && onClose(true);
    } catch (err) {
      console.error(`Error saving ${label}:`, err);
      alert(err?.response?.data?.error || `Error saving ${label}.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={shellStyle}>
        <div style={{ textAlign: "center", marginBottom: "18px" }}>
          <p
            style={{
              letterSpacing: "0.35em",
              fontSize: "0.8rem",
              color: "var(--erp-modal-muted, #aebbd0)",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          >
            Room rate studio
          </p>
          <h2 style={{ margin: 0, fontSize: "1.35rem", color: "var(--erp-modal-title, #f2f6ff)" }}>Add {label}</h2>
          <p style={{ marginTop: "6px", color: "var(--erp-modal-muted, #aebbd0)" }}>
            Apply stay constraint overrides across single dates, ranges, weekdays or variable spans.
          </p>
        </div>

        <div style={contentWrapperStyle}>
          <form onSubmit={handleSubmit}>
            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Mode & dates</h3>
                  <p style={sectionTextStyle}>Choose the mode you want to work in.</p>
                </div>
              </div>
              <div style={tabContainerStyle}>
                {modeTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    style={tabButtonStyle(localData.mode === tab.key)}
                    onClick={() => setLocalData((prev) => ({ ...prev, mode: tab.key }))}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>{localData.mode === "single" ? "Date" : "Start Date"}</label>
                  <input
                    type="date"
                    className="form-control"
                    style={inputFieldStyle}
                    value={localData.start_date}
                    onChange={(event) => setLocalData((prev) => ({ ...prev, start_date: event.target.value }))}
                    required
                  />
                </div>
                {localData.mode !== "single" && (
                  <div>
                    <label style={labelStyle}>End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      style={inputFieldStyle}
                      value={localData.end_date}
                      onChange={(event) => setLocalData((prev) => ({ ...prev, end_date: event.target.value }))}
                      required
                    />
                  </div>
                )}
              </div>
            </section>

            {(localData.mode === "single" || localData.mode === "range") && (
              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <div>
                    <h3 style={sectionTitleStyle}>{label}</h3>
                    <p style={sectionTextStyle}>Enter the value or clear it entirely for the selected dates.</p>
                  </div>
                  <label style={{ color: "var(--erp-modal-text, #dfe8f7)" }}>
                    <input
                      type="checkbox"
                      style={{ marginRight: "6px" }}
                      checked={localData.remove}
                      onChange={(event) =>
                        setLocalData((prev) => ({
                          ...prev,
                          remove: event.target.checked,
                          ...(event.target.checked ? { value: "" } : {}),
                        }))
                      }
                    />
                    Clear constraint
                  </label>
                </div>
                <div style={fieldGridStyle}>
                  <div>
                    <input
                      type="number"
                      min="1"
                      style={inputFieldStyle}
                      value={localData.value}
                      disabled={localData.remove}
                      onChange={(event) => setLocalData((prev) => ({ ...prev, value: event.target.value }))}
                      required={!localData.remove}
                    />
                  </div>
                </div>
              </section>
            )}

            {localData.mode === "weekdays" && (
              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <div>
                    <h3 style={sectionTitleStyle}>Weekdays</h3>
                    <p style={sectionTextStyle}>Assign a value per weekday or mark it to clear.</p>
                  </div>
                </div>
                <div style={helperGridStyle}>
                  {WEEKDAYS.map((day) => {
                    const valueKey = `wd_value_${day.key}`;
                    const clearKey = `wd_clear_${day.key}`;
                    const isClear = !!localData[clearKey];
                    return (
                      <div
                        key={day.key}
                        style={{
                          borderRadius: "12px",
                          padding: "12px",
                          border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
                          background: "var(--erp-modal-input, #152344)",
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "var(--erp-modal-title, #f2f6ff)", marginBottom: "6px" }}>{day.label}</div>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                          <input
                            type="number"
                            min="1"
                            placeholder={label}
                            className="form-control"
                            style={{ ...inputFieldStyle, flex: 1 }}
                            value={localData[valueKey] || ""}
                            disabled={isClear}
                            onChange={(event) =>
                              setLocalData((prev) => ({ ...prev, [valueKey]: event.target.value }))
                            }
                          />
                          <button
                            type="button"
                            style={{
                              borderRadius: "999px",
                              border: "1px solid var(--erp-modal-danger, #9B3445)",
                              background: isClear ? "var(--erp-danger-bg, rgba(155, 52, 69, 0.2))" : "transparent",
                              color: "var(--erp-danger, #ffc7cf)",
                              padding: "6px 12px",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setLocalData((prev) => ({
                                ...prev,
                                [clearKey]: !prev[clearKey],
                                ...(prev[clearKey] ? {} : { [valueKey]: "" }),
                              }))
                            }
                          >
                            {isClear ? "Cleared" : "Clear"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {localData.mode === "customRange" && localData.start_date && localData.end_date && (
              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <div>
                    <h3 style={sectionTitleStyle}>Variable range entries</h3>
                    <p style={sectionTextStyle}>
                      Fill a value per day and use the Clear toggle to remove it.
                    </p>
                  </div>
                </div>
                {(() => {
                  const start = parseIsoLocalDate(localData.start_date);
                  const end = parseIsoLocalDate(localData.end_date);
                  if (!start || !end) return null;
                  const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                  if (diffDays > 30) {
                    return (
                      <div style={sectionTextStyle}>
                        Only up to 30 days can be entered at once.
                      </div>
                    );
                  }
                  const days = Array.from({ length: diffDays }, (_, idx) => {
                    const date = new Date(start);
                    date.setDate(start.getDate() + idx);
                    return { date, idx };
                  });
                  return (
                    <div style={{ maxHeight: "260px", overflowY: "auto", marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                      {days.map(({ date, idx }) => {
                        const iso = toIsoLocalDate(date);
                        const weekday = WEEKDAYS[date.getDay() === 0 ? 6 : date.getDay() - 1].label;
                        const valueKey = `multi_value_${idx}`;
                        const clearKey = `multi_clear_${idx}`;
                        const isClear = !!localData[clearKey];
                        return (
                          <div
                            key={iso}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 150px",
                              gap: "12px",
                              alignItems: "center",
                              padding: "10px 12px",
                              borderRadius: "12px",
                              background: "var(--erp-modal-input, #152344)",
                              border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
                            }}
                          >
                            <div>
                              <strong>{iso}</strong> · {weekday}
                            </div>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <input
                                type="number"
                                min="1"
                                placeholder={label}
                                className="form-control"
                                style={{ ...inputFieldStyle, flex: 1, margin: 0 }}
                                value={localData[valueKey] || ""}
                                disabled={isClear}
                                onChange={(event) =>
                                  setLocalData((prev) => ({ ...prev, [valueKey]: event.target.value }))
                                }
                              />
                              <button
                                type="button"
                                style={{
                                  borderRadius: "999px",
                                  border: "1px solid var(--erp-modal-danger, #9B3445)",
                                  background: isClear ? "var(--erp-danger-bg, rgba(155, 52, 69, 0.2))" : "transparent",
                                  color: "var(--erp-danger, #ffc7cf)",
                                  padding: "6px 12px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  setLocalData((prev) => ({
                                    ...prev,
                                    [clearKey]: !prev[clearKey],
                                    ...(prev[clearKey] ? {} : { [valueKey]: "" }),
                                  }))
                                }
                              >
                                {isClear ? "Cleared" : "Clear"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </section>
            )}

            <div style={actionsRowStyle}>
              <button type="submit" style={primaryButtonStyle} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save constraint"}
              </button>
              <button type="button" style={secondaryButtonStyle} onClick={() => onClose && onClose(false)} disabled={isSaving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {isSaving && <div style={savingOverlayStyle}>Saving {label.toLowerCase()}…</div>}
    </div>
  );
}
